import { ChangeDetectionStrategy, Component, contentChildren, InjectionToken, input } from '@angular/core';

import { AccordionPanelComponent } from './accordion-panel.component';

// Injection token for the accordion component
export const ACCORDION = new InjectionToken<AccordionComponent>('ACCORDION');

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-accordion',
  template: `<ng-content></ng-content>`,
  styleUrls: ['./accordion.component.scss'],
  providers: [{ provide: ACCORDION, useExisting: AccordionComponent }],
})
export class AccordionComponent {
  /**
   * Whether multiple panels can be expanded at the same time
   */
  allowMultiple = input<boolean>(false);

  /**
   * All accordion panels inside this accordion using contentChildren signal
   */
  panels = contentChildren(AccordionPanelComponent);

  /**
   * Focus the first panel header
   */
  focusFirstPanel(): void {
    const currentPanels = this.panels();
    if (currentPanels.length > 0) {
      currentPanels[0].focus();
    }
  }

  /**
   * Focus the last panel header
   */
  focusLastPanel(): void {
    const currentPanels = this.panels();
    if (currentPanels.length > 0) {
      currentPanels[currentPanels.length - 1].focus();
    }
  }

  /**
   * Focus the next panel header
   */
  focusNextPanel(currentId: string): void {
    const currentPanels = this.panels();
    if (!currentPanels.length) return;

    const currentIndex = currentPanels.findIndex((p) => p.id() === currentId);

    if (currentIndex > -1 && currentIndex < currentPanels.length - 1) {
      currentPanels[currentIndex + 1].focus();
    }
  }

  /**
   * Focus the previous panel header
   */
  focusPreviousPanel(currentId: string): void {
    const currentPanels = this.panels();
    if (!currentPanels.length) return;

    const currentIndex = currentPanels.findIndex((p) => p.id() === currentId);

    if (currentIndex > 0) {
      currentPanels[currentIndex - 1].focus();
    }
  }

  /**
   * Toggle a panel by ID programmatically
   */
  togglePanel(id: string): void {
    const currentPanels = this.panels();
    if (!currentPanels.length) return;

    if (!this.allowMultiple()) {
      currentPanels.forEach((panel) => {
        if (panel.id() === id) {
          panel.toggle();
        } else {
          panel.setExpanded(false);
        }
      });
      return;
    }

    const panel = currentPanels.find((p) => p.id() === id);
    if (panel) {
      panel.toggle();
    }
  }
}
